import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import useConfirmation from "../Hooks/useConfirmation";
import FAIcon from "../UI/FAIcon/FAIcon";
const FieldArray = ({ name, addLabel, singleItemLabel, pluralItemLabel, children, }) => {
    const { fields, append, remove } = useFieldArray({
        name,
    });
    const { getValues, unregister } = useFormContext();
    const [confirmation, confirmModals] = useConfirmation();
    /**
     * React-hook-form has a bug where controlled components in field arrays aren't unregistered when the field
     * array item is removed.  In addition, any additional changes to fields after a field array method (like append or remove)
     * is called have to happen in a subsequent render.  So, if we could somehow find all the fields we needed to unregister,
     * we couldn't do something like
     * ```
     * remove(index)
     * unregister(all controlled fields we need to unregister)
     * ```
     *
     * instead, we have to do something like
     * ```
     * remove(index)
     * // cause another render and remove the fields we need to unregister on that render
     * ```
     *
     * That's what this state variable is for.  We set shouldFire when we want to correct the bug.
     * It's an object and not just a boolean in case at some point we need to provide additional information about what to do in the next render.
     */
    const [fixFormBug, setFixFormBug] = React.useState({
        shouldFire: false,
    });
    React.useEffect(() => {
        if (fixFormBug.shouldFire) {
            setFixFormBug({
                shouldFire: false,
            });
            const currentFieldsNested = getValues({ nest: true })[name]; // an array of the current fieds
            // This gets us the field with "stringified" property paths.  For example {field : [1, 2, 3]} turns into
            // {"field[0]": 1, "field[1]": 2, "field[2]": 3}.  These keys are exactly what we need.  We need to pass
            // the stringified paths to unregister, so we'll use this to refer to the properties we should delete.
            const currentFields = getValues();
            // First handle case that we removed all the fields
            if (currentFieldsNested &&
                currentFieldsNested.length !== 0 &&
                fields.length === 0) {
                // unregister everything that starts with name and then has a delimiter
                unregister(Object.keys(currentFields).filter((field) => field.split("[")[0] === name));
            }
            if (currentFieldsNested.length > fields.length) {
                // manually unregister all the fields in the field we deleted.
                const stuff = Object.keys(currentFields).filter((field) => {
                    const split = field.split(/[\[\]]/);
                    // react-hook-form puts the field that we deleted with the controlled fields as the last element in
                    // the field array for some reason.  We make sure that the index is the last one.
                    return (split[0] === name &&
                        parseInt(split[1], 10) === currentFieldsNested.length - 1);
                });
                unregister(stuff);
            }
        }
    }, [fields.length, fixFormBug.shouldFire, getValues, name, unregister]);
    const [isConfirming, setIsConfirming] = React.useState(false);
    return (_jsxs(_Fragment, { children: [confirmModals, fields.map((field, index) => {
                const makeName = (fieldName) => {
                    if (name === "id") {
                        console.error("An id field will break react-hook-form uncontrolled components in field arrays.  Consider naming id something else");
                    }
                    return `${name}[${index}].${fieldName}`;
                };
                return (_jsxs(React.Fragment, { children: [children({ makeName }), _jsx("button", { disabled: isConfirming, className: "btn btn-danger", type: "button", onClick: () => {
                                setIsConfirming(true);
                                confirmation(`Are you sure you want to remove this ${singleItemLabel}?`)
                                    .then((yes) => {
                                    if (yes) {
                                        remove(index);
                                        setFixFormBug({
                                            shouldFire: true,
                                        });
                                    }
                                })
                                    .finally(() => {
                                    setIsConfirming(false);
                                });
                            }, children: "Remove" }), _jsx("hr", {})] }, field.id));
            }), _jsxs("div", { className: "d-flex justify-content-between w-100", children: [_jsxs("button", { className: "btn btn-outline-primary", 
                        // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
                        onClick: () => append(), type: "button", children: [_jsx(FAIcon, { icon: "plus", addMarginRight: true }), addLabel] }), fields.length > 0 && (_jsx(_Fragment, { children: _jsx("button", { disabled: isConfirming, className: "btn btn-danger ml-2", onClick: () => {
                                setIsConfirming(true);
                                confirmation(`Are you sure you want to remove all ${pluralItemLabel}?`)
                                    .then((yes) => {
                                    if (yes) {
                                        remove();
                                        setFixFormBug({
                                            shouldFire: true,
                                        });
                                    }
                                })
                                    .finally(() => {
                                    setIsConfirming(false);
                                });
                            }, type: "button", children: "Remove all" }) }))] })] }));
};
export default FieldArray;
